import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Loader2, Paperclip, Send, Users, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { accountApi } from '@/api/commercial/accounts';
import type { Historical } from '@/api/commercial/historicals';
import { historicalApi } from '@/api/commercial/historicals';
import { accountUploadApi } from '@/api/upload/account-uploads';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileUpload } from '@/components/ui/file-upload';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/auth-context';
import { useToast } from '@/hooks/use-toast';
import { downloadFromUrl } from '@/lib/utils';

interface AccountHistoryTabProps {
  accountId: string;
}

interface PendingFile {
  name: string;
  url: string;
  id: string;
  key: string;
}

interface PendingContact {
  id: string;
  name: string;
  type: string;
  contact: string;
}

const ACCEPTED_TYPES = '.pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.csv';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function formatDatePart(dateString: string): string {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(dateString));
}

function formatTimePart(dateString: string): string {
  return new Intl.DateTimeFormat('pt-BR', { timeStyle: 'medium' }).format(new Date(dateString));
}

/** Normalize contacts to always be an array (backward compat with old single-object format) */
function normalizeContacts(
  contacts: Historical['contacts'],
): { id: string; name: string; type: string; contact: string }[] {
  if (!contacts) return [];
  if (Array.isArray(contacts)) return contacts;
  return [contacts];
}

/** Get file extension from a filename */
function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot !== -1 ? filename.substring(lastDot) : '';
}

/** Get file name without extension */
function getFileBaseName(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot !== -1 ? filename.substring(0, lastDot) : filename;
}

function TimelineCard({ entry }: { entry: Historical }) {
  const authorName = entry.author?.name ?? 'Sistema';
  const { toast } = useToast();

  const handleDownload = async (file: NonNullable<Historical['file']>) => {
    try {
      const downloadKey = file.key || file.id;
      const fileName = file.name || 'download';
      if (downloadKey) {
        const url = await accountUploadApi.getDownloadUrl(downloadKey);
        await downloadFromUrl(url, fileName);
      } else if (file.url) {
        await downloadFromUrl(file.url, fileName);
      }
    } catch {
      toast({
        title: 'Erro',
        description: 'Falha ao obter link de download.',
        variant: 'destructive',
      });
    }
  };

  const contactsList = normalizeContacts(entry.contacts);

  return (
    <div className="flex items-start gap-3 rounded-md border p-4">
      <Avatar className="h-9 w-9 shrink-0">
        {entry.author?.avatarUrl && <AvatarImage src={entry.author.avatarUrl} alt={authorName} />}
        <AvatarFallback>{authorName}</AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <p className="text-sm">
          <span className="font-medium">{authorName}</span>{' '}
          <span className="text-muted-foreground">{entry.title}</span>
        </p>
        {entry.editedFields && entry.editedFields.length > 0 && (
          <div className="mt-1 space-y-0.5">
            {entry.editedFields.map((field) => (
              <p
                key={`${field.key}-${field.oldValue}-${field.newValue}`}
                className="text-muted-foreground text-sm"
              >
                Campo &apos;<span className="font-bold">{field.key}</span>&apos; de{' '}
                <span className="font-medium">{field.oldValue}</span> para{' '}
                <span className="font-medium">{field.newValue}</span>
              </p>
            ))}
          </div>
        )}
        {(entry.file?.name || contactsList.length > 0) && (
          <div className="mt-3 space-y-2 border-t pt-3">
            {entry.file?.name && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-xs shrink-0">Anexo:</span>
                <Badge variant="secondary" className="gap-1.5">
                  <FileText className="h-3 w-3" />
                  {entry.file.name}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => handleDownload(entry.file!)}
                >
                  <Download className="h-3 w-3 mr-1" />
                  Download
                </Button>
              </div>
            )}
            {contactsList.length > 0 && (
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground text-xs shrink-0 mt-0.5">Contato:</span>
                <div className="space-y-0.5">
                  {contactsList.map((c) => (
                    <p
                      key={`${c.id}-${c.type}-${c.contact}`}
                      className="text-muted-foreground text-sm"
                    >
                      <Users className="mr-1 inline h-3 w-3" />
                      {c.name} — {c.type}: {c.contact}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="text-muted-foreground shrink-0 text-right text-xs whitespace-nowrap">
        <p>{formatDatePart(entry.created_at)}</p>
        <p>{formatTimePart(entry.created_at)}</p>
      </div>
    </div>
  );
}

const CONTACT_TYPES = ['Celular', 'Telefone Residencial', 'Telefone Comercial', 'Email'] as const;

function ContactSelectionDialog({
  accountId,
  open,
  onOpenChange,
  onSelect,
}: {
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (contact: PendingContact) => void;
}) {
  const [selectedItem, setSelectedItem] = useState<PendingContact | null>(null);
  const [outroName, setOutroName] = useState('');
  const [outroType, setOutroType] = useState('');
  const [outroContact, setOutroContact] = useState('');
  const [useOutro, setUseOutro] = useState(false);

  const { data: contacts, isLoading } = useQuery({
    queryKey: ['account-contacts', accountId],
    queryFn: () => accountApi.findContacts(accountId),
    enabled: open && !!accountId,
  });

  const selectExistingContact = (
    contact: { id: string; name: string },
    item: { type: string; contact: string },
  ) => {
    const pending: PendingContact = {
      id: contact.id,
      name: contact.name,
      type: item.type,
      contact: item.contact,
    };
    const isSame =
      selectedItem?.id === contact.id &&
      selectedItem?.type === item.type &&
      selectedItem?.contact === item.contact;
    if (isSame) {
      setSelectedItem(null);
    } else {
      setSelectedItem(pending);
      setUseOutro(false);
      setOutroName('');
      setOutroType('');
      setOutroContact('');
    }
  };

  const isSelected = (contactId: string, type: string, contact: string) =>
    !useOutro &&
    selectedItem?.id === contactId &&
    selectedItem?.type === type &&
    selectedItem?.contact === contact;

  const outroFilled = outroName.trim() && outroType && outroContact.trim();

  const handleConfirm = () => {
    if (useOutro && outroFilled) {
      onSelect({
        id: crypto.randomUUID(),
        name: outroName.trim(),
        type: outroType,
        contact: outroContact.trim(),
      });
    } else if (selectedItem) {
      onSelect(selectedItem);
    } else {
      return;
    }
    resetState();
    onOpenChange(false);
  };

  const resetState = () => {
    setSelectedItem(null);
    setOutroName('');
    setOutroType('');
    setOutroContact('');
    setUseOutro(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetState();
    onOpenChange(nextOpen);
  };

  const canConfirm = (useOutro && outroFilled) || (!useOutro && selectedItem);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Selecionar Contato</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="max-h-72 space-y-3 overflow-y-auto pr-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {contacts && contacts.length > 0 ? (
                  contacts.map((contact) => (
                    <div key={contact.id} className="rounded-md border p-3">
                      <p className="text-sm font-medium mb-1">{contact.name}</p>
                      <p className="text-muted-foreground text-xs mb-2">
                        {contact.positionOrRelation}
                      </p>
                      {contact.contactItems && contact.contactItems.length > 0 ? (
                        <div className="space-y-1.5 pl-1">
                          {contact.contactItems.map((item) => {
                            const selected = isSelected(contact.id, item.type, item.contact);
                            return (
                              <button
                                type="button"
                                key={item.id}
                                className={`flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors ${
                                  selected
                                    ? 'bg-gray-100 ring-1 ring-gray-900'
                                    : 'hover:bg-muted/50'
                                }`}
                                onClick={() => selectExistingContact(contact, item)}
                              >
                                <div
                                  className={`h-3.5 w-3.5 shrink-0 rounded-full border-2 ${
                                    selected
                                      ? 'border-black bg-black'
                                      : 'border-muted-foreground/40'
                                  }`}
                                >
                                  {selected && (
                                    <div className="h-full w-full rounded-full border-2 border-white" />
                                  )}
                                </div>
                                <span className="text-muted-foreground">
                                  {item.type}: {item.contact}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-xs pl-1">
                          Nenhum item de contato cadastrado.
                        </p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground py-4 text-center text-sm">
                    Nenhum contato encontrado para esta conta.
                  </p>
                )}

                {/* Outro — last item inside the scroll */}
                <div className="rounded-md border p-3">
                  <p className="text-sm font-medium mb-2">Outro</p>
                  <div className="space-y-2">
                    <Input
                      placeholder="Nome"
                      value={outroName}
                      onChange={(e) => {
                        setOutroName(e.target.value);
                        if (e.target.value) {
                          setUseOutro(true);
                          setSelectedItem(null);
                        }
                      }}
                    />
                    <div className="flex gap-2">
                      <Select
                        value={outroType}
                        onValueChange={(val) => {
                          setOutroType(val);
                          setUseOutro(true);
                          setSelectedItem(null);
                        }}
                      >
                        <SelectTrigger className="w-[180px] shrink-0">
                          <SelectValue placeholder="Tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          {CONTACT_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Contato"
                        value={outroContact}
                        onChange={(e) => {
                          setOutroContact(e.target.value);
                          if (e.target.value) {
                            setUseOutro(true);
                            setSelectedItem(null);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConfirm} disabled={!canConfirm}>
              Confirmar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AccountHistoryTab({ accountId }: AccountHistoryTabProps) {
  const [page, setPage] = useState(1);
  const [note, setNote] = useState('');
  const [pendingFile, setPendingFile] = useState<PendingFile | null>(null);
  const [pendingContact, setPendingContact] = useState<PendingContact | null>(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [customFileName, setCustomFileName] = useState('');
  const limit = 20;
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoGrow = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['account-history', accountId, page],
    queryFn: () => historicalApi.findByAccount(accountId, { page, limit }),
    enabled: !!accountId,
  });

  const createNote = useMutation({
    mutationFn: (payload: {
      file?: { name: string; url: string; id: string; key: string };
      contacts?: { id: string; name: string; type: string; contact: string }[];
    }) =>
      historicalApi.create({
        accountId,
        title: note.trim(),
        type: 'manual',
        author: { name: user?.name ?? '' },
        ...(payload.file && { file: payload.file }),
        ...(payload.contacts && payload.contacts.length > 0 && { contacts: payload.contacts }),
      }),
    onSuccess: () => {
      setNote('');
      setPendingFile(null);
      setPendingContact(null);
      queryClient.invalidateQueries({ queryKey: ['account-history', accountId] });
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    },
  });

  const handleSubmit = () => {
    if (!note.trim() && !pendingFile && !pendingContact) return;
    createNote.mutate({
      file: pendingFile ?? undefined,
      contacts: pendingContact ? [pendingContact] : undefined,
    });
  };

  const handleFileSelect = (files: File[] | null) => {
    const newFiles = files || [];
    setSelectedFiles(newFiles);
    if (newFiles.length > 0) {
      setCustomFileName(getFileBaseName(newFiles[0].name));
    } else {
      setCustomFileName('');
    }
  };

  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) return;
    const file = selectedFiles[0];

    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: 'Erro',
        description: `Arquivo "${file.name}" excede o limite de 10MB.`,
        variant: 'destructive',
      });
      return;
    }

    const ext = getFileExtension(file.name);
    const displayName = customFileName.trim() ? `${customFileName.trim()}${ext}` : file.name;

    setIsUploading(true);
    try {
      const { uploadUrl, key, id, fileUrl } = await accountUploadApi.prepareUpload(
        accountId,
        displayName,
        displayName,
        file.type,
        'attachment',
      );

      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!response.ok) {
        throw new Error('Failed to upload file to S3');
      }

      const etag = response.headers.get('etag') || undefined;

      await accountUploadApi.confirmUpload({
        id,
        accountId,
        name: displayName,
        key,
        category: 'attachment',
        extension: ext.replace('.', ''),
        contentType: file.type,
        sizeInBytes: file.size,
        etag,
      });

      setPendingFile({ name: displayName, url: fileUrl, id, key });
      queryClient.invalidateQueries({ queryKey: ['account-uploads', accountId] });
      toast({ title: 'Sucesso', description: 'Arquivo enviado com sucesso.' });
      setIsUploadDialogOpen(false);
      setSelectedFiles([]);
      setCustomFileName('');
    } catch {
      toast({ title: 'Erro', description: 'Falha ao enviar arquivo.', variant: 'destructive' });
    } finally {
      setIsUploading(false);
    }
  };

  const canSubmit = note.trim() || pendingFile || pendingContact;

  return (
    <div className="mx-auto space-y-4">
      {/* Composer input */}
      <div className="rounded-md border">
        <Textarea
          ref={textareaRef}
          placeholder="Adicionar observação manual..."
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            autoGrow();
          }}
          rows={3}
          className="border-0 shadow-none focus-visible:ring-0"
          style={{ overflow: 'hidden' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />

        {/* Pending chips */}
        {(pendingFile || pendingContact) && (
          <div className="space-y-1.5 px-3 pb-2">
            {pendingFile && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-xs shrink-0">Anexo:</span>
                <Badge variant="secondary" className="gap-1.5">
                  <FileText className="h-3 w-3" />
                  {pendingFile.name}
                  <button
                    type="button"
                    onClick={() => setPendingFile(null)}
                    className="ml-0.5 rounded-full hover:bg-muted"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              </div>
            )}
            {pendingContact && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-xs shrink-0">Contato:</span>
                <Badge variant="secondary" className="gap-1.5">
                  <Users className="h-3 w-3" />
                  {pendingContact.name} ({pendingContact.type})
                  <button
                    type="button"
                    onClick={() => setPendingContact(null)}
                    className="ml-0.5 rounded-full hover:bg-muted"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              </div>
            )}
          </div>
        )}

        {/* Bottom bar */}
        <div className="flex items-center justify-between border-t px-3 py-2">
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsUploadDialogOpen(true)}
            >
              <Paperclip className="h-4 w-4 mr-1" />
              Anexar
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsContactDialogOpen(true)}
            >
              <Users className="h-4 w-4 mr-1" />
              Contato
            </Button>
          </div>
          <Button size="sm" onClick={handleSubmit} disabled={!canSubmit || createNote.isPending}>
            {createNote.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Enviar
          </Button>
        </div>
      </div>

      {/* Timeline */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : !data || data.docs.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          Nenhum registro encontrado
        </div>
      ) : (
        <>
          <div className="relative space-y-0">
            {data.docs.map((entry, index) => (
              <div key={entry.id}>
                <TimelineCard entry={entry} />
                {index < data.docs.length - 1 && (
                  <div className="flex justify-start pl-[1.35rem]">
                    <div className="border-muted-foreground/25 h-3 border-l-2" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <Pagination
            currentPage={page}
            totalPages={data.totalPages || 1}
            totalItems={data.total || 0}
            limit={data.limit || limit}
            onPageChange={setPage}
          />
        </>
      )}

      {/* File Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Anexar Arquivo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <FileUpload
              handleFile={handleFileSelect}
              accept={ACCEPTED_TYPES}
              label="Selecione o arquivo"
            />
            {selectedFiles.length > 0 && (
              <div>
                <label htmlFor="history-file-name" className="text-sm font-medium leading-none">
                  Nome do arquivo
                </label>
                <div className="mt-1.5 flex">
                  <Input
                    id="history-file-name"
                    value={customFileName}
                    onChange={(e) => setCustomFileName(e.target.value)}
                    placeholder="Nome personalizado"
                    className="rounded-r-none border-r-0"
                  />
                  <span className="inline-flex items-center rounded-r-md border border-l-0 border-input bg-muted px-3 text-sm text-muted-foreground">
                    {getFileExtension(selectedFiles[0].name)}
                  </span>
                </div>
              </div>
            )}
            <p className="text-muted-foreground text-xs">
              Formatos aceitos: PDF, PNG, JPG, DOC, DOCX, XLS, XLSX, CSV. Máximo 10MB.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsUploadDialogOpen(false);
                  setSelectedFiles([]);
                  setCustomFileName('');
                }}
                disabled={isUploading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleFileUpload}
                disabled={selectedFiles.length === 0 || isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Confirmar'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Selection Dialog */}
      <ContactSelectionDialog
        accountId={accountId}
        open={isContactDialogOpen}
        onOpenChange={setIsContactDialogOpen}
        onSelect={setPendingContact}
      />
    </div>
  );
}
